import { constantCase, isNumber, isString } from '@art-suite/art-core-ts';
import { getGroupName } from '@kaiizen/kai-reports';
import { getEffectiveScanValue, ScanTemplateValue } from '@kaiizen/kai-scan-template-system';
import React from 'react';

import { presentMeasurementValue } from '@/Client/Presenters';
import { useScanEditingContextSelector } from '@/Client/State';
import { TOOLTIP_SECONDARY_TEXT } from '@/Client/Widgets';

const IDENTIFIER_PATTERN = /[A-Za-z_]\w*/g;

/** A measurement's value name as prose: room values drop the "room" prefix (roomWallArea → wall area). */
const presentMeasurementName = (valueName: string) => constantCase(valueName.replace(/^room(?=[A-Z])/, ''), ' ').toLowerCase();

/** An equation-driven measurement is taken once it reads above zero; until then there's nothing to measure a purchase by. */
export const getMeasurementIsTaken = (measurement: unknown): boolean => isNumber(measurement) && measurement > 0;

/**
 * What an equation-driven measurement's tooltip says: the group measurement it reads (and that
 * measurement's value), and that it's set on the Measurements tab (the value itself links
 * there). An equation that does more than read the measurement shows beneath, in the same prose names.
 */
export const useMeasurementTooltipContent = (groupId: string, measurementTemplate: ScanTemplateValue | undefined, measurementIsTaken: boolean) => {
  const equation = isString(measurementTemplate?.value) ? measurementTemplate.value.trim() : undefined;
  const [measurementValueName] = equation?.match(IDENTIFIER_PATTERN) ?? [];
  const groupName = useScanEditingContextSelector(({ scanRoot }) => {
    const group = scanRoot?.groupsById[groupId];
    return group ? getGroupName(group) : undefined;
  }, [groupId]);
  const measuredValue = useScanEditingContextSelector(
    ({ scanRoot }) => measurementValueName ? getEffectiveScanValue(scanRoot?.groupsById[groupId]?.values?.[measurementValueName]) : undefined,
    [groupId, measurementValueName],
  );

  if (!equation || !measurementValueName) return undefined;

  const measurementName = presentMeasurementName(measurementValueName);
  const measured = isNumber(measuredValue) ? ` (${presentMeasurementValue(measuredValue, measurementTemplate?.type)})` : '';
  const presentedEquation = equation.replace(IDENTIFIER_PATTERN, presentMeasurementName).replace(/\*/g, '×');
  const groupsMeasurement = `${groupName ? `${groupName}'s ` : 'the '}${measurementName}`;

  return (
    <>
      {measurementIsTaken ? `Based on ${groupsMeasurement}${measured}.` : `There's no measurement yet for ${groupsMeasurement}.`}
      {equation !== measurementValueName && <p css={TOOLTIP_SECONDARY_TEXT}>{`Calculated as ${presentedEquation}.`}</p>}
      <p css={TOOLTIP_SECONDARY_TEXT}>{measurementIsTaken ? 'Change it on the Measurements tab.' : 'Fill it in on the Measurements tab.'}</p>
    </>
  );
};
